import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Home, ArrowLeft, Layout, Palette, FileText, ShoppingBag,
  Tag, Newspaper, Scale, PenTool, Layers, ImageIcon,
  ChevronRight, ExternalLink, Sparkles
} from 'lucide-react';

const VisualPageEditor = () => {
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState('homepage');

  // Editable sections with direct links
  const editableSections = [
    {
      id: 'homepage',
      title: 'Homepage',
      icon: Home,
      color: 'bg-blue-50 border-blue-200',
      iconColor: 'text-blue-600',
      items: [
        { name: 'Hero Banner', description: 'Hauptbanner mit Bild und Text', link: '/admin/homepage-manager', section: 'hero' },
        { name: 'Saisonale Themes', description: 'Oster, Weihnachten, etc.', link: '/admin/seasonal-themes' },
        { name: 'Abschnitt-Vorlagen', description: 'Text+Bild, Galerie, Banner', link: '/admin/section-templates' },
        { name: 'Homepage Sektionen', description: 'Reihenfolge und Inhalte', link: '/admin/homepage-manager' },
      ]
    },
    {
      id: 'products',
      title: 'Produkte & Kategorien',
      icon: ShoppingBag,
      color: 'bg-green-50 border-green-200',
      iconColor: 'text-green-600',
      items: [
        { name: 'Alle Produkte', description: 'Produkte verwalten', link: '/admin/products' },
        { name: 'Kategorien', description: 'Kategorien bearbeiten', link: '/admin/categories' },
        { name: 'Rabattcodes', description: 'Gutscheine erstellen', link: '/admin/discount-codes' },
      ]
    },
    {
      id: 'design',
      title: 'Design & Farben',
      icon: Palette,
      color: 'bg-purple-50 border-purple-200',
      iconColor: 'text-purple-600',
      items: [
        { name: 'Shop-Design', description: 'Farben und Schriften', link: '/admin/design' },
        { name: 'Saisonale Themes', description: 'Saisonale Farben anpassen', link: '/admin/seasonal-themes' },
        { name: 'Logo & Icons', description: 'Shop-Logo hochladen', link: '/admin/design' },
      ]
    },
    {
      id: 'content',
      title: 'Inhalte & Seiten',
      icon: FileText,
      color: 'bg-yellow-50 border-yellow-200',
      iconColor: 'text-yellow-600',
      items: [
        { name: 'Blog-Beiträge', description: 'Blog verwalten', link: '/admin/blog' },
        { name: 'Rechtliches', description: 'Impressum, AGB, Datenschutz', link: '/admin/legal-pages' },
        { name: 'Kontaktseite', description: 'Kontaktinformationen', link: '/admin/contact-settings' },
      ]
    },
    {
      id: 'marketing',
      title: 'Marketing',
      icon: Sparkles,
      color: 'bg-pink-50 border-pink-200',
      iconColor: 'text-pink-600',
      items: [
        { name: 'Newsletter', description: 'Newsletter erstellen', link: '/admin/newsletter' },
        { name: 'Kampagnen', description: 'Auto-Kampagnen verwalten', link: '/admin/campaigns' },
        { name: 'Social Media', description: 'Social Links teilen', link: '/admin/social-media' },
      ]
    },
  ];

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: '#E8E0D5' }}>
      {/* Top Navigation Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin')}
              className="rounded-none"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline ml-1">Zurück</span>
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
                <PenTool className="h-5 w-5 text-amber-600" />
                Seiten bearbeiten
              </h1>
              <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">Wählen Sie einen Bereich zum Bearbeiten</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-none"
            onClick={() => window.open('/', '_blank')}
          >
            <ExternalLink className="h-4 w-4" />
            <span className="hidden sm:inline ml-1">Shop ansehen</span>
          </Button>
        </div>
      </div>
      
      {/* Info Banner */}
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
        <p className="text-sm text-amber-800 flex items-center gap-2">
          <Layers className="h-4 w-4 flex-shrink-0" />
          <span>Tippen Sie auf einen Bereich, um die Bearbeitungsoptionen zu sehen</span>
        </p>
      </div>
      
      <div className="p-4 space-y-3">
        {editableSections.map((section) => (
          <Card 
            key={section.id} 
            className={`border ${expandedSection === section.id ? section.color : 'bg-white'} transition-all duration-200`}
          >
            {/* Section Header - Always clickable */}
            <button
              onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
              className="w-full text-left"
            >
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${section.color}`}>
                      <section.icon className={`h-5 w-5 ${section.iconColor}`} />
                    </div>
                    <span className="text-base font-semibold">{section.title}</span>
                  </div>
                  <ChevronRight 
                    className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                      expandedSection === section.id ? 'rotate-90' : ''
                    }`} 
                  />
                </CardTitle>
              </CardHeader>
            </button>
            
            {/* Expandable Items */}
            {expandedSection === section.id && (
              <CardContent className="pt-0 pb-3 px-4">
                <div className="space-y-2 mt-2">
                  {section.items.map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => navigate(item.link)}
                      className="w-full flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100 hover:border-gray-300 hover:shadow-sm transition-all active:scale-[0.98]"
                    >
                      <div className="text-left">
                        <p className="font-medium text-gray-900 text-sm">{item.name}</p>
                        <p className="text-xs text-gray-500">{item.description}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </button>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        ))}
        
        {/* Quick Preview Section */}
        <Card className="mt-6 bg-white border">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="flex items-center gap-3 text-base">
              <div className="p-2 rounded-lg bg-gray-100">
                <ExternalLink className="h-5 w-5 text-gray-600" />
              </div>
              Schnellvorschau
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="grid grid-cols-2 gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="rounded-none justify-start"
                onClick={() => window.open('/', '_blank')}
              >
                <Home className="h-4 w-4 mr-2" />
                Homepage
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="rounded-none justify-start"
                onClick={() => window.open('/products', '_blank')}
              >
                <ShoppingBag className="h-4 w-4 mr-2" />
                Produkte
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="rounded-none justify-start"
                onClick={() => window.open('/blog', '_blank')}
              >
                <Newspaper className="h-4 w-4 mr-2" />
                Blog
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="rounded-none justify-start"
                onClick={() => window.open('/contact', '_blank')}
              >
                <FileText className="h-4 w-4 mr-2" />
                Kontakt
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VisualPageEditor;
